import * as Sentry from "@sentry/react";

import dayjs from "@/components/dayjsExtended";
import i18n from "@/i18n";
import { sendToSentry } from "@/utils/sentry";

import { apiBase, fetchUsingApi } from "../core/apiClient";
import { ApiResponse } from "../core/types";
import { AccountAssignmentList, AssignmentEntityType } from "../models/account";
import {
  AssignAccountRequest,
  AssignAccountResponse,
  UnassignAccountRequest,
  UserInfo,
  UserInfoRaw,
} from "../models/account";

const convertUserInfo = (data: UserInfoRaw): UserInfo => {
  return {
    ...data,
    createdAtTimestamp: dayjs(data.createdAt),
  };
};

export const fetchUserInfo = async (): Promise<ApiResponse<UserInfo>> => {
  const result = await fetchUsingApi(`${apiBase}/account/v1/info`, "GET", convertUserInfo);
  if (result.success) {
    try {
      Sentry.setUser({
        id: result.result.userId.toString(),
        email: result.result.email,
        name: result.result.name,
      });
      await i18n.changeLanguage(result.result.language);
    } catch (error) {
      sendToSentry(error as Error, {
        additionalContext: {
          result,
          function: "fetchUserInfo",
        },
      });
    }
  }
  return result;
};

export const getAccountAssignments = async (
  entityType: AssignmentEntityType,
  entityId: number,
): Promise<ApiResponse<AccountAssignmentList>> => {
  return fetchUsingApi(`${apiBase}/account/v1/assignments/${entityType}/${entityId}`, "GET", (data) => data);
};

export const sendBuildingUserPasswordResetEmail = async (
  buildingId: number,
  userId: number,
): Promise<ApiResponse<void>> => {
  return fetchUsingApi(
    `${apiBase}/account/v1/buildings/${buildingId}/users/${userId}/send-password-reset`,
    "POST",
    () => {},
  );
};

export const assignAccountToEntity = async (
  entityType: AssignmentEntityType,
  entityId: number,
  data: AssignAccountRequest,
): Promise<ApiResponse<AssignAccountResponse>> => {
  return fetchUsingApi(
    `${apiBase}/account/v1/assignments/${entityType}/${entityId}`,
    "POST",
    (response) => response,
    JSON.stringify(data),
  );
};

export const unassignAccountFromEntity = async (
  entityType: AssignmentEntityType,
  entityId: number,
  data: UnassignAccountRequest,
): Promise<ApiResponse<void>> => {
  return fetchUsingApi(
    `${apiBase}/account/v1/assignments/${entityType}/${entityId}`,
    "DELETE",
    () => {},
    JSON.stringify(data),
  );
};
